import atob from 'atob'

export const state = () => ({
  id: null,
  webUrl: null,
  lineItems: null,
  customAttributes: null,
  subtotalPrice: null,
  totalPrice: null,
  totalTax: null,
})

export const getters = {
  getCheckoutId(state) {
    return state.id
  },
  getCheckout(state) {
    return {
      id: state.id,
      webUrl: state.webUrl,
      lineItems: state.lineItems,
      customAttributes: state.customAttributes,
      subtotalPrice: state.subtotalPrice,
      totalPrice: state.totalPrice,
      totalTax: state.totalTax,
    }
  },
  getTotalQuantity(state) {
    let total = 0
    if (
      state.lineItems &&
      state.lineItems.edges &&
      state.lineItems.edges.length > 0
    ) {
      state.lineItems.edges.forEach(({ node }) => {
        total += node.quantity
      })
    }
    return total
  },
  getCheckoutLineItems(state) {
    return state.lineItems && state.lineItems.edges ? state.lineItems.edges : []
  },
}

export const mutations = {
  setCheckout(
    state,
    {
      id,
      webUrl,
      lineItems,
      customAttributes,
      subtotalPrice,
      totalPrice,
      totalTax,
    }
  ) {
    state.id = id
    state.webUrl = webUrl
    state.lineItems = lineItems
    state.customAttributes = customAttributes
    state.subtotalPrice = subtotalPrice
    state.totalPrice = totalPrice
    state.totalTax = totalTax
  },
}

export const actions = {
  async initialize({ dispatch, rootGetters }) {
    try {
      const customer = rootGetters['customer/getCustomer']
      if (
        customer &&
        customer.lastIncompleteCheckout &&
        customer.lastIncompleteCheckout.id &&
        !customer.lastIncompleteCheckout.completedAt
      )
        await dispatch('retreiveCheckout')
      else await dispatch('createCheckout')
    } catch (e) {
      this.app.$sentryException(e, {
        source: 'STORE checkout',
        level: 'fatal',
        extra: 'ERROR STORE - checkout/initialize(): ' + e,
      })
    }
  },
  async createCheckout({ dispatch, commit, rootGetters }) {
    try {
      const cartLines = rootGetters['cart/getCartLines']
      const products = cartLines.length > 0 ? extractVariantsIds(cartLines) : []
      const { checkout, userErrors } = await this.app.$shopify.checkout.create(
        products
      )
      if (userErrors.length > 0) throw new Error(userErrors)
      await dispatch('cart/clear', null, { root: true })
      commit('setCheckout', checkout)
    } catch (e) {
      this.app.$sentryException(e, {
        source: 'STORE checkout',
        level: 'fatal',
        extra: 'ERROR STORE - checkout/createCheckout(): ' + e,
      })
    }
  },
  async retreiveCheckout({ commit, dispatch, rootGetters }) {
    try {
      const customer = rootGetters['customer/getCustomer']
      const cartLines = rootGetters['cart/getCartLines']
      const products = cartLines.length > 0 ? extractVariantsIds(cartLines) : []
      if (products?.length > 0) {
        const updatedCheckout = await this.app.$shopify.checkout.addLineItems(
          customer.lastIncompleteCheckout.id,
          products
        )
        await dispatch('cart/clear', null, { root: true })
        commit('setCheckout', updatedCheckout.checkout)
      } else {
        const calledCheckout = await this.app.$shopify.checkout.retreive(
          customer.lastIncompleteCheckout.id
        )
        await dispatch('cart/clear', null, { root: true })
        commit('setCheckout', calledCheckout)
      }
    } catch (e) {
      this.app.$sentryException(e, {
        source: 'STORE checkout',
        level: 'fatal',
        extra: 'ERROR STORE - checkout/retreiveCheckout(): ' + e,
      })
    }
  },
  async updateLineItemsToCheckout({ getters, commit }, { products }) {
    try {
      const checkoutId = getters.getCheckoutId
      const checkoutLineItems = getters.getCheckoutLineItems
      const lineItems = findLineItemsToUpdate(products, checkoutLineItems)

      const { checkout, checkoutUserErrors } =
        await this.app.$shopify.checkout.updateLineItems(checkoutId, lineItems)
      if (checkoutUserErrors.length > 0)
        console.log('checkoutUser Errors:', checkoutUserErrors)
      else commit('setCheckout', checkout)
    } catch (e) {
      this.app.$sentryException(e, {
        source: 'STORE checkout',
        level: 'fatal',
        extra: 'ERROR STORE - checkout/updateLineItemsToCheckout(): ' + e,
      })
    }
  },
  clear({ commit }) {
    try {
      commit('setCheckout', {
        id: null,
        webUrl: null,
        lineItems: null,
        customAttributes: null,
        subtotalPrice: null,
        totalPrice: null,
        totalTax: null,
      })
    } catch (e) {
      this.app.$sentryException(e, {
        source: 'STORE checkout',
        level: 'fatal',
        extra: 'ERROR STORE - checkout/clear(): ' + e,
      })
    }
  },
  async addProductsToCheckout({ getters, dispatch, commit }, { products }) {
    try {
      if (getters.getCheckoutId)
        await dispatch('addLineItemsToCheckout', { products })
      commit(
        'notification/setAddToCartNotification',
        {
          error: false,
          product: {
            product: products[0].product,
            selectedVariant: products[0].variant,
            quantity: products[0].variant.quantity,
          },
        },
        { root: true }
      )
    } catch (e) {
      commit(
        'notification/setAddToCartNotification',
        {
          error: true,
          product: {
            product: products[0].product,
            selectedVariant: products[0].variant,
            quantity: products[0].variant.quantity,
          },
        },
        { root: true }
      )
      this.app.$sentryException(e, {
        source: 'STORE checkout',
        level: 'fatal',
        extra: 'ERROR STORE - checkout/addProductsToCheckout(): ' + e,
      })
    }
  },
  async goToCheckoutWithCheckout(
    { getters, rootGetters, dispatch },
    { GAIdSession }
  ) {
    try {
      const checkout = getters.getCheckout
      const GAId = GAIdSession ? `&${GAIdSession}` : null
      const currentLang = this.app.i18n.locale.split('-')[0]
      const currentCountry = this.app.i18n.locale.split('-')[1]
      let redirectionUrl = null
      if (checkout.webUrl) {
        redirectionUrl = `${checkout.webUrl}?locale=${currentLang}${GAId}`
      }
      const customer = rootGetters['customer/getBasicCustomer']
      if (customer) {
        await dispatch('disassociateCheckout')
        const { data } = await this.$axios.post(
          window.location.origin + '/api/multipass',
          {
            customer,
            checkoutUrl: redirectionUrl,
            country: currentCountry,
          }
        )
        window.location.href = data
      }
    } catch (e) {
      this.app.$sentryException(e, {
        source: 'STORE checkout',
        level: 'fatal',
        extra: 'ERROR STORE - checkout/goToCheckoutWithCheckout(): ' + e,
      })
    }
  },
  //
  async addLineItemsToCheckout({ getters, commit }, { products }) {
    try {
      const checkoutId = getters.getCheckoutId
      const lineItems = buildLineItemsArray(products)

      const { checkout, checkoutUserErrors } =
        await this.app.$shopify.checkout.addLineItems(checkoutId, lineItems)
      if (checkoutUserErrors.length > 0)
        console.log('checkoutUser Errors:', checkoutUserErrors)
      else commit('setCheckout', checkout)
    } catch (e) {
      throw new Error('ERROR STORE - checkout/addLineItemsToCheckout()', e)
    }
  },
  async disassociateCheckout({ getters }) {
    try {
      const checkoutId = getters.getCheckoutId
      if (checkoutId)
        await this.app.$shopify.checkout.disassociateCheckout(checkoutId)
    } catch (e) {
      throw new Error('ERROR STORE - checkout/disassociateCheckout()', e)
    }
  },
}

export function extractVariantsIds(cartLines) {
  const ids = []
  cartLines.forEach(({ node }) => {
    ids.push({
      variantId: node.merchandise.id,
      quantity: node.quantity,
      customAttributes: node.attributes,
    })
  })
  return ids
}
export function buildCustomAttributes(product, variant) {
  const ret = [
    {
      key: 'product_categorization',
      value: product.productCategorization || 'not set',
    },
    {
      key: 'color_family',
      value: product.normalized_color
        ? product.normalized_color.toLowerCase()
        : 'not set',
    },
    {
      key: 'season',
      value: product.season || 'not set',
    },
    {
      key: 'color',
      value: product.sample_color
        ? product.sample_color
        : product.normalized_color,
    },
    { key: 'size', value: variant.size_name || '' },
    {
      key: 'site_link',
      value: product.modelGroup
        ? `${product.modelGroup}?variant=${product.internal_type}-${product.internal_color_code}`
        : product.handle,
    },
    { key: 'title', value: product.title || '' },
    {
      key: 'image',
      value:
        product.medias && product.medias.length > 0
          ? product.medias[0].src
          : '',
    },
  ]
  const discountPrice = product.discount_price
    ? {
        key: 'discount_price',
        value: product.discount_price ? product.discount_price : '',
      }
    : null
  if (discountPrice) ret.push(discountPrice)
  const isFinalSale = product.isFinalSale
    ? {
        key: 'is_final_sale',
        value: product.isFinalSale ? 'true' : 'false',
      }
    : null

  if (isFinalSale) ret.push(isFinalSale)
  return ret
}
export function buildLineItemsArray(products) {
  const ret = []
  products.forEach(({ product, variant, quantity }) => {
    ret.push({
      variantId: variant.id.includes('gid://shopify/')
        ? variant.id
        : atob(variant.id),
      quantity: variant.quantity,
      customAttributes: buildCustomAttributes(product, variant),
    })
  })
  return ret
}
export function findLineItemsToUpdate(products, checkoutLines) {
  const matchingcheckoutLines = []
  products.forEach((product) => {
    const { node } = checkoutLines.find((line) => {
      return (
        line.node &&
        line.node.variant &&
        line.node.variant.id === product.variant.id
      )
    })
    if (node) {
      matchingcheckoutLines.push({
        id: node.id,
        variantId: node.variant.id,
        quantity: product.quantity,
        customAttributes: node.customAttributes,
      })
    }
  })
  return matchingcheckoutLines
}
